import { useState } from 'react';
import { InfoIcon, PowerIcon, PowerOffIcon, UserCogIcon } from 'lucide-react';
import { AdminRole } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/page-header';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import { useAdminAccounts, useSetAccountActive } from '@/lib/admin-queries';
import { authStore } from '@/lib/auth-store';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const ACCOUNT_COLUMNS = 5;

/**
 * Admin hisoblari (§4.13).
 *
 * Faqat o'chirish va yoqish. Hisob YARATISH serverdagi `admin:create` skriptida qoladi: parol
 * o'rnatish yo'lini panelga chiqarish o'g'irlangan sessiyani yangi hisob ochish imkoniga
 * aylantirardi.
 */
export function AccountsPage() {
  const accounts = useAdminAccounts();
  const setActive = useSetAccountActive();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const me = authStore.get()?.admin.id ?? null;

  const rows = accounts.data?.items ?? [];

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={strings.accounts.title} description={strings.accounts.subtitle} />

      <Card className="max-w-5xl">
        <CardHeader>
          <CardTitle>{strings.accounts.title}</CardTitle>
          <CardDescription className="flex items-start gap-2">
            <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {strings.accounts.hint}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {accounts.isError ? <ErrorNotice error={accounts.error} /> : null}
          {setActive.isError ? <ErrorNotice error={setActive.error} /> : null}

          {accounts.isSuccess && rows.length === 0 ? (
            <EmptyState title={strings.app.empty} icon={UserCogIcon} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{strings.accounts.columnEmail}</TableHead>
                  <TableHead>{strings.accounts.columnRole}</TableHead>
                  <TableHead>{strings.accounts.columnState}</TableHead>
                  <TableHead>{strings.accounts.columnLastLogin}</TableHead>
                  <TableHead>{strings.accounts.columnActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.isPending ? <TableSkeleton columns={ACCOUNT_COLUMNS} /> : null}
                {rows.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">{account.email}</TableCell>
                    <TableCell>
                      <Badge variant={account.role === AdminRole.ADMIN ? 'default' : 'secondary'}>
                        {account.role === AdminRole.ADMIN
                          ? strings.accounts.roleAdmin
                          : strings.accounts.roleOperator}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={account.isActive ? 'success' : 'outline'}>
                        {account.isActive ? strings.accounts.active : strings.accounts.inactive}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular whitespace-nowrap">
                      {account.lastLoginAt
                        ? formatTashkent(account.lastLoginAt)
                        : strings.accounts.never}
                    </TableCell>
                    <TableCell>
                      {/*
                        O'z hisobini o'chirish tugmasi umuman chizilmaydi. Server ham buni rad
                        etadi — ikkalasi kerak: tugma bo'lmasa xato ham bo'lmaydi, ammo qoidani
                        ushlab turadigan joy server.
                      */}
                      {account.id === me ? (
                        <span className="text-muted-foreground text-xs">
                          {strings.accounts.self}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            className="h-8 w-44"
                            placeholder={strings.accounts.reason}
                            aria-label={`${strings.accounts.reason} — ${account.email}`}
                            value={reasons[account.id] ?? ''}
                            onChange={(event) =>
                              setReasons({ ...reasons, [account.id]: event.target.value })
                            }
                          />
                          <Button
                            size="sm"
                            // Yoqish — xavfsiz amal, o'chirish esa yo'q. Brend rangi qizil
                            // bo'lgani uchun ikkalasi ham birlamchi tugma bo'lsa, ular ekranda
                            // bir xil ko'rinardi.
                            variant={account.isActive ? 'destructive' : 'outline'}
                            disabled={setActive.isPending}
                            onClick={() =>
                              setActive.mutate({
                                adminId: account.id,
                                isActive: !account.isActive,
                                reason: (reasons[account.id] ?? '').trim() || undefined,
                              })
                            }
                          >
                            {account.isActive ? (
                              <PowerOffIcon aria-hidden="true" />
                            ) : (
                              <PowerIcon aria-hidden="true" />
                            )}
                            {account.isActive
                              ? strings.accounts.deactivate
                              : strings.accounts.activate}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
